import { useIntl } from 'react-intl'
import type { TextFieldTypes } from 'react-md'
import { CircularProgress, SelectField, TextField } from 'react-md'
import type { Action } from 'ui/components/ViiluFullPageDialog'
import ViiluFullPageDialog from 'ui/components/ViiluFullPageDialog'
import {
	CANCEL_BUTTON_LABEL,
	GENERIC_ERROR_NOTIFICATION,
	SAVE_BUTTON_LABEL,
} from 'ui/messages'
import type { RegistryMeter } from '../types'
import { Fragment, useEffect, useState } from 'react'
import DatePickerISO from 'ui/components/DatePickerISO'
import { meterReadingFormHeaders } from './constants'
import { getOptions } from '../utils'
import type { ApartmentSelectOptions } from '@viilu-web/shared/types/apartment'
import styled from 'styled-components'
import { StandardDialogContent } from 'ui/StyledComponents/BasicComponents'
import { _createMeterReading } from 'state/finance-meter-registry-actions'
import type { MeterReadingForm } from './types'
import useMeterReading from './useMeterReading'
import * as notifyActions from 'state/notifyActions'
import { useDispatch } from 'react-redux'
import { useReturnToLastUrl } from 'util/hooks/useReturnToLastUrl'
import { useForm, Controller } from 'react-hook-form'
import { ErrorMessage } from 'ui/ManagerFinance/utils/components'
import useDialogs from 'ui/components/dialogs/useDialogs'
import { meterRegistryFormSchemaLocalized } from './validation'
import { LoadingContainer } from 'ui/ManagerFinance/components'
import LoadingState from 'ui/components/LoadingState'
import { useZodValidationResolver } from 'util/hooks/useZodValidationResolver'

const FormContainer = styled.div`
	margin-left: 24px;
	margin-right: 24px;
	padding-bottom: 64px;
`

type Props = {
	uuid?: string
	onHide: () => void
	onHideURL?: string
	refresh: () => void
	selectedCompanyUUID: string
	apartmentOptions: ApartmentSelectOptions[]
	registryMeters: RegistryMeter[]
}

const MeterReadingDialog = ({
	uuid,
	onHide,
	onHideURL,
	refresh,
	selectedCompanyUUID,
	apartmentOptions,
	registryMeters,
}: Props) => {
	const intl = useIntl()
	const [processing, setProcessing] = useState(false)
	const dispatch = useDispatch()
	const { alert } = useDialogs()
	const disabled = !!uuid

	const { data, loading } = useMeterReading(uuid)

	const resolver = useZodValidationResolver<MeterReadingForm>(
		meterRegistryFormSchemaLocalized(intl, registryMeters),
	)

	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<MeterReadingForm>({
		resolver,
		defaultValues: {
			companyUUID: selectedCompanyUUID,
			status: null,
			date: '',
			value: null,
			meterUUID: '',
		},
		mode: 'onBlur',
		shouldUnregister: false,
	})

	useEffect(() => {
		if (data) {
			setValue('companyUUID', data.companyUUID)
			setValue('status', data.status)
			setValue('date', data.date)
			setValue('value', data.value)
			setValue('meterUUID', data.meterUUID)
		}
	}, [data, setValue])

	const { targetUrl, navigateBack } = useReturnToLastUrl(onHideURL)

	const hide = () => {
		if (targetUrl) {
			navigateBack()
		} else {
			onHide()
		}
	}

	const onSubmit = async (formValues: MeterReadingForm) => {
		if (uuid) return

		try {
			setProcessing(true)
			const result = await _createMeterReading(formValues)

			if (!result || result.ok === false) {
				dispatch(notifyActions.error(GENERIC_ERROR_NOTIFICATION(intl)))
				setProcessing(false)
				return
			}

			dispatch(
				notifyActions.success(
					uuid
						? intl.formatMessage({
								defaultMessage: 'Mittarilukema on päivitetty.',
								description:
									'Notification text of meter reading has been updated',
						  })
						: intl.formatMessage({
								defaultMessage: 'Mittarilukema on lisätty.',
								description:
									'Notification text of meter reading has been added',
						  }),
				),
			)
			refresh()
			hide()
		} catch (error) {
			alert('Something went wrong.')
			setProcessing(false)
		}
	}

	const renderForm = () => {
		if (loading) {
			return (
				<LoadingContainer>
					<LoadingState />
				</LoadingContainer>
			)
		}

		return (
			<FormContainer>
				{meterReadingFormHeaders.map((header) => {
					const { title, key, type, show, required } = header
					if (!show) return null

					if (type === 'date') {
						return (
							<Fragment key={key}>
								<Controller
									key={key}
									name={key as keyof MeterReadingForm}
									control={control}
									render={({ field: { value, onChange } }) => (
										<DatePickerISO
											style={{ width: '100%' }}
											id={key}
											label={title(intl)}
											value={(value as string) || ''}
											onChange={(value) => onChange(value)}
											disabled={disabled}
											required={required}
										/>
									)}
								/>
								{errors[key]?.message && (
									<ErrorMessage>{errors[key]?.message}</ErrorMessage>
								)}
							</Fragment>
						)
					} else if (type.toLowerCase().includes('options')) {
						return (
							<Fragment key={key}>
								<Controller
									key={key}
									name={key as keyof MeterReadingForm}
									control={control}
									render={({ field: { value, onChange } }) => (
										<Fragment>
											<p className="text-subtle">{title(intl)}</p>
											<SelectField
												id={key}
												placeholder={title(intl)}
												value={(value as string) || ''}
												menuItems={getOptions(type, {
													intl,
													apartmentOptions,
													registryMeters,
												})}
												position={SelectField.Positions.BELOW}
												simplifiedMenu={false}
												listHeightRestricted
												disabled={disabled}
												style={{
													width: '100%',
													background: '#fafafa',
													marginBottom: '10px',
												}}
												onChange={(value) => {
													onChange(value)
												}}
												required={required}
											/>
										</Fragment>
									)}
								/>
								{errors[key]?.message && (
									<ErrorMessage>{errors[key]?.message}</ErrorMessage>
								)}
							</Fragment>
						)
					} else {
						return (
							<Fragment key={key}>
								<Controller
									key={key}
									name={key as keyof MeterReadingForm}
									control={control}
									render={({ field: { value, onChange } }) => (
										<TextField
											id={key}
											label={title(intl)}
											floating
											lineDirection="center"
											fullWidth
											value={(value as typeof type) || ''}
											onChange={(value) =>
												onChange(type === 'number' ? Number(value) : value)
											}
											type={(type as TextFieldTypes) || undefined}
											disabled={disabled}
											error={!!errors[key]}
											required={required}
										/>
									)}
								/>
								{errors[key]?.message && (
									<ErrorMessage>{errors[key]?.message}</ErrorMessage>
								)}
							</Fragment>
						)
					}
				})}
			</FormContainer>
		)
	}

	const addTitle = intl.formatMessage({
		defaultMessage: 'Lisää mittarilukema',
		description: 'Title for the meter reading dialog: Add meter reading',
	})

	const editTitle = intl.formatMessage({
		defaultMessage: 'Muokkaa mittarilukemaa',
		description: 'Title for the meter reading dialog: edit meter reading',
	})

	const actions: Action[] = [
		{
			id: 'dialog-cancel',
			children: CANCEL_BUTTON_LABEL(intl),
			onClick: hide,
		},
		{
			id: 'dialog-save',
			secondary: true,
			children: processing ? (
				<CircularProgress id="progress" style={{ marginTop: 0 }} />
			) : (
				SAVE_BUTTON_LABEL(intl)
			),
			onClick: handleSubmit(onSubmit),
		},
	]

	return (
		<ViiluFullPageDialog
			id="meter-reading-dialog"
			title={uuid ? editTitle : addTitle}
			visible
			actions={disabled ? [] : actions}
			focusOnMount={false}
			toolbarStyle={{
				background: 'var(--color-secondary-dark)',
			}}
			onHide={hide}
			onHideURL={onHideURL}
		>
			<StandardDialogContent>{renderForm()}</StandardDialogContent>
		</ViiluFullPageDialog>
	)
}

export default MeterReadingDialog
